import { useEffect, useRef, RefObject } from 'react'
import { trackSectionView } from '../lib/analytics'

/**
 * Hook to track when a section becomes visible in the viewport
 * Uses Intersection Observer for efficient visibility detection
 * Each section is only tracked once per page load
 * 
 * @param sectionName - Name of the section for analytics
 * @param threshold - Percentage of section that must be visible (0-1), default 0.3
 */
export function useSectionTracking<T extends HTMLElement = HTMLElement>(sectionName: string, threshold = 0.3): RefObject<T> {
  const ref = useRef<T>(null)
  const hasTracked = useRef(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTracked.current) {
            hasTracked.current = true
            trackSectionView(sectionName)
          }
        })
      },
      {
        threshold,
        rootMargin: '0px',
      }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [sectionName, threshold])

  return ref
}

/**
 * Component wrapper for section tracking
 * Useful when you can't easily use the hook directly
 */
export function TrackedSection({
  name,
  children,
  className,
  as = 'section',
  threshold = 0.3,
}: {
  name: string
  children: React.ReactNode
  className?: string
  as?: 'section' | 'div' | 'article' | 'aside' | 'main' | 'header' | 'footer' | 'nav'
  threshold?: number
}) {
  const ref = useSectionTracking<HTMLDivElement>(name, threshold)
  
  // Use div as wrapper to ensure consistent ref type
  return (
    <div ref={ref} className={className} data-section={as}>
      {children}
    </div>
  )
}

